<template>
  <div class="transactions-modal-overlay" @click="closeModal">
    <div class="transactions-modal" @click.stop>
      <!-- Cabeçalho do modal -->
      <div class="modal-header">
        <h3 class="modal-title">
          <i :class="transactionType === 'credit' ? 'fa-solid fa-arrow-up' : 'fa-solid fa-arrow-down'"></i>
          {{ transactionType === 'credit' ? 'Banca Recebeu (+)' : 'Banca Pagou (-)' }}
        </h3>
        <button class="close-btn" @click="closeModal">
          <i class="fa-solid fa-times"></i>
        </button>
      </div>

      <!-- Informações da região -->
      <div class="modal-subheader">
        <div class="region-info">
          <i class="fa-solid fa-map-marker-alt"></i>
          <span class="region-name">{{ regionName }}</span>
        </div>
        <div class="period-info">
          <i class="fa-solid fa-calendar"></i>
          <span>{{ formatDate(dateBegin) }} até {{ formatDate(dateEnd) }}</span>
        </div>
      </div>

      <!-- Conteúdo do modal -->
      <div class="modal-content">
        <!-- Loading state -->
        <div v-if="loading" class="loading-state">
          <div class="loading-spinner">
            <i class="fa-solid fa-spinner fa-spin"></i>
            <span>Carregando transações...</span>
          </div>
        </div>

        <!-- Error state -->
        <div v-else-if="error" class="error-state">
          <div class="error-icon">
            <i class="fa-solid fa-exclamation-triangle"></i>
          </div>
          <h4 class="error-title">Erro ao carregar transações</h4>
          <p class="error-message">{{ error }}</p>
          <button class="retry-btn" @click="loadTransactions">
            <i class="fa-solid fa-refresh"></i>
            Tentar novamente
          </button>
        </div>

        <!-- Empty state -->
        <div v-else-if="!transactions || transactions.length === 0" class="empty-state">
          <div class="empty-icon">
            <i class="fa-solid fa-inbox"></i>
          </div>
          <h4 class="empty-title">Nenhuma transação encontrada</h4>
          <p class="empty-message">Não há transações para o período selecionado.</p>
        </div>

        <!-- Tabela de transações -->
        <div v-else class="transactions-table-container">
          <table class="transactions-table">
            <thead>
              <tr>
                <th>Data</th>
                <th>Vendedor</th>
                <th>Descrição</th>
                <th>Valor</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              <tr 
                v-for="transaction in transactions" 
                :key="transaction.id"
                class="transaction-row"
              >
                <td>
                  <div class="date-cell">
                    <i class="fa-solid fa-calendar-day"></i>
                    <span>{{ formatDateTime(transaction.date) }}</span>
                  </div>
                </td>
                <td>
                  <div class="seller-cell">
                    <i class="fa-solid fa-user"></i>
                    <span>{{ transaction.seller || 'Banca' }}</span>
                  </div>
                </td>
                <td>
                  <span class="description-text">{{ transaction.description || '-' }}</span>
                </td>
                <td>
                  <span :class="['currency-value', transactionType === 'credit' ? 'currency-positive' : 'currency-negative']">
                    {{ setCurrencyString(transaction.value) }}
                  </span>
                </td>
                <td>
                  <span :class="['status-badge', transaction.confirmed ? 'status-confirmed' : 'status-pending']">
                    <i :class="transaction.confirmed ? 'fa-solid fa-check-circle' : 'fa-solid fa-clock'"></i>
                    {{ transaction.confirmed ? 'Confirmado' : 'Pendente' }}
                  </span>
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr class="total-row">
                <td colspan="3">
                  <div class="total-label">
                    <i class="fa-solid fa-calculator"></i>
                    <span>Total</span>
                  </div>
                </td>
                <td>
                  <span :class="['currency-value', 'total-value', transactionType === 'credit' ? 'currency-positive' : 'currency-negative']">
                    {{ setCurrencyString(totalValue) }}
                  </span>
                </td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <!-- Ações do modal -->
      <div v-if="!loading && !error" class="modal-actions">
        <button class="btn-primary action-btn close-btn-action" @click="closeModal">
          <i class="fa-solid fa-times"></i>
          Fechar
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import mainService from "@/services/mainService";
import { toast } from "@/composables/useToast";
import { handleErrorMessage } from "@/services/config";
import jsFunctions from "@/services/jsFunctions";

export default {
  name: "TransactionsDetailModal",
  props: {
    regionId: {
      type: Number,
      required: true
    },
    regionName: {
      type: String,
      required: true
    },
    transactionType: {
      type: String,
      required: true,
      validator: value => ['credit', 'debit'].includes(value)
    },
    dateBegin: {
      type: String,
      required: true
    },
    dateEnd: {
      type: String,
      required: true
    },
    isLpTable: {
      type: Boolean,
      default: false
    },
    isDescargaRegion: {
      type: Boolean,
      default: false
    }
  },
  emits: ['close'],
  data() {
    return {
      loading: true,
      error: null,
      transactions: []
    };
  },
  computed: {
    totalValue() {
      return this.transactions.reduce((sum, t) => sum + (t.value || 0), 0);
    }
  },
  mounted() {
    this.loadTransactions();
  },
  methods: {
    async loadTransactions() {
      this.loading = true;
      this.error = null;

      try {
        // Lógica de filtro:
        // - Região Normal, Acerto L.P (isLpTable=true): SEM vendedor → onlyBancaTransactions=true
        // - Região Normal, tabela principal (isLpTable=false): COM vendedor → onlyBancaTransactions=false
        // - Região Descarga: usa IsDescargaRegion=true + IsLpTableDescarga para separar fluxos
        const onlyBanca = !this.isDescargaRegion && this.isLpTable;

        const request = {
          RegionId: this.regionId,
          financialTransactionsDateBegin: this.dateBegin + 'T00:00:00',
          financialTransactionsDateEnd: this.dateEnd + 'T23:59:59',
          type: this.transactionType === 'credit' ? 'ENTRADA' : 'SAIDA',
          onlyBancaTransactions: onlyBanca,
          isDescargaRegion: this.isDescargaRegion,
          isLpTableDescarga: this.isDescargaRegion && this.isLpTable,
          pageIndex: 0
        };

        const response = await mainService.searchFinancialTransactionsByRegion(request);

        if (response.data && response.data.result) {
          this.transactions = response.data.result.financialTransactions || 
                             response.data.result.FinancialTransactions || 
                             [];
        } else {
          this.transactions = [];
        }
      } catch (error) {
        this.error = handleErrorMessage(error);
      } finally {
        this.loading = false;
      }
    },

    formatDate(dateString) {
      try {
        if (!dateString) return 'N/A';
        // Backend returns dates without Z/offset - already local time
        const date = new Date(dateString);
        return date.toLocaleDateString('pt-BR');
      } catch (error) {
        return dateString;
      }
    },

    formatDateTime(dateTimeString) {
      try {
        if (!dateTimeString) return 'N/A';
        const date = new Date(dateTimeString);
        return date.toLocaleDateString('pt-BR') + ' ' + date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
      } catch (error) {
        return dateTimeString;
      }
    },

    setCurrencyString(value) {
      return jsFunctions.setCurrencyString(value);
    },

    closeModal() {
      this.$emit('close');
    }
  }
};
</script>

<style scoped>
.transactions-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
  backdrop-filter: blur(2px);
}

.transactions-modal {
  background: var(--card-background);
  border-radius: 12px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  max-width: 1000px;
  width: 100%;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.modal-title {
  margin: 0;
  font-size: 1.3rem;
  display: flex;
  align-items: center;
  gap: 10px;
}
.modal-content{
  max-width: 100%!important;
  width: 100%!important;
}

.close-btn {
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  transition: background 0.3s ease;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

.modal-subheader {
  background: var(--bg-secondary);
  padding: 15px 20px;
  border-bottom: 1px solid var(--border-color);
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
}

.region-info,
.period-info {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--text-primary);
  font-size: 0.95rem;
}

.region-name {
  font-weight: 600;
}

.modal-content {
  flex: 1;
  overflow-y: auto;
  background: var(--bg-primary);
}

/* Estados de loading, erro e vazio */
.loading-state,
.error-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  text-align: center;
}

.loading-spinner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
  color: var(--accent-color);
}

.loading-spinner i {
  font-size: 2.5rem;
}

.loading-spinner span {
  color: var(--text-secondary);
}

.error-icon,
.empty-icon {
  font-size: 3rem;
  margin-bottom: 15px;
}

.error-icon {
  color: var(--danger-color);
}

.empty-icon {
  color: var(--text-muted);
}

.error-title,
.empty-title {
  color: var(--text-primary);
  margin-bottom: 10px;
}

.error-message,
.empty-message {
  color: var(--text-secondary);
  margin-bottom: 20px;
}

.retry-btn {
  background: var(--accent-color);
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: background 0.3s ease;
}

.retry-btn:hover {
  background: var(--accent-hover);
}

/* Tabela de transações */
.transactions-table-container {
  overflow-x: auto;
}

.transactions-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.95rem;
}

.transactions-table th {
  background: var(--bg-secondary);
  color: var(--text-primary);
  padding: 12px 16px;
  text-align: left;
  font-weight: 600;
  border-bottom: 2px solid var(--border-color);
  white-space: nowrap;
}

[data-theme="dark"] .transactions-table th {
  background: var(--bg-tertiary);
}

.transactions-table td {
  padding: 12px 16px;
  text-align: left;
  border-bottom: 1px solid var(--border-color);
  color: var(--text-primary);
}

.transaction-row:nth-child(even) {
  background: rgba(0, 0, 0, 0.02);
}

[data-theme="dark"] .transaction-row:nth-child(even) {
  background: rgba(255, 255, 255, 0.02);
}

.transaction-row:hover {
  background: rgba(96, 165, 250, 0.1);
}

/* Células específicas */
.date-cell,
.seller-cell {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--text-primary);
}

.date-cell i,
.seller-cell i {
  color: var(--text-muted);
  font-size: 0.9rem;
}

.description-text {
  color: var(--text-secondary);
}

/* Valores monetários */
.currency-value {
  font-family: 'Consolas', 'Monaco', monospace;
  font-weight: 600;
  font-size: 1rem;
}

.currency-positive {
  color: #10b981 !important;
}

.currency-negative {
  color: #ef4444 !important;
}

/* Status badge */
.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 0.85rem;
  font-weight: 500;
}

.status-confirmed {
  background: rgba(16, 185, 129, 0.1);
  color: #10b981;
}

.status-pending {
  background: rgba(245, 158, 11, 0.1);
  color: #f59e0b;
}

/* Total row */
.total-row {
  background: var(--bg-secondary);
  font-weight: 600;
}

[data-theme="dark"] .total-row {
  background: var(--bg-tertiary);
}

.total-row td {
  border-top: 2px solid var(--accent-color);
  font-weight: 600;
  padding: 15px 16px;
}

.total-label {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--accent-color);
  font-weight: 600;
}

.total-value {
  font-size: 1.1rem;
}

/* Modal actions */
.modal-actions {
  padding: 20px;
  background: var(--bg-secondary);
  border-top: 1px solid var(--border-color);
  display: flex;
  gap: 10px;
  justify-content: center;
}

.action-btn {
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
}

.close-btn-action {
  background: var(--secondary-btn-color);
  color: white;
}

.close-btn-action:hover {
  background: var(--secondary-btn-hover);
}

/* Responsivo */
@media (max-width: 768px) {
  .transactions-modal {
    margin: 10px;
    max-height: 95vh;
  }

  .modal-subheader {
    flex-direction: column;
    align-items: flex-start;
  }

  .transactions-table {
    font-size: 0.85rem;
  }

  .transactions-table th,
  .transactions-table td {
    padding: 8px 12px;
  }
}

@media (max-width: 480px) {
  .modal-title {
    font-size: 1.1rem;
  }

  .transactions-table th,
  .transactions-table td {
    padding: 6px 8px;
    font-size: 0.8rem;
  }
}
</style> 